import React, { useMemo, useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DEFAULT_CONTENT_ORDER,
  CONTENT_ELEMENT_LABELS,
  CONTENT_ELEMENT_GROUPS,
  GROUP_COLORS,
  getEffectiveContentOrder,
  isElementActive,
} from '../../constants/contentOrder';

/**
 * Static item render — used both in sortable slots and in the DragOverlay.
 */
function ItemContent({ id, isActive }) {
  const group = CONTENT_ELEMENT_GROUPS[id];
  const groupColor = GROUP_COLORS[group] || '#888';
  const label = CONTENT_ELEMENT_LABELS[id] || id;

  return (
    <div className={`content-order-item${!isActive ? ' content-order-item-inactive' : ''}`}>
      <span className="content-order-grip" aria-hidden="true">
        <svg width="10" height="14" viewBox="0 0 10 14" fill="currentColor">
          <circle cx="2.5" cy="2" r="1.2" />
          <circle cx="7.5" cy="2" r="1.2" />
          <circle cx="2.5" cy="7" r="1.2" />
          <circle cx="7.5" cy="7" r="1.2" />
          <circle cx="2.5" cy="12" r="1.2" />
          <circle cx="7.5" cy="12" r="1.2" />
        </svg>
      </span>
      <span
        className="content-order-dot"
        style={{ backgroundColor: isActive ? groupColor : 'rgba(255,255,255,0.15)' }}
      />
      <span className="content-order-label">{label}</span>
      <span
        className="content-order-group"
        style={{ color: isActive ? groupColor : 'rgba(255,255,255,0.2)' }}
      >
        {group}
      </span>
    </div>
  );
}

/**
 * Sortable item — stays in the DOM at all times.
 * When dragging, it becomes invisible (opacity 0) but preserves its space.
 */
function SortableItem({ id, isActive }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="content-order-slot"
    >
      <ItemContent id={id} isActive={isActive} />
    </div>
  );
}

/**
 * ContentOrderEditor — drag-to-reorder content elements in the editor panel.
 */
export function ContentOrderEditor({ data, onChange }) {
  const [activeId, setActiveId] = useState(null);

  const contentOrder = useMemo(
    () => getEffectiveContentOrder(data.contentOrder),
    [data.contentOrder]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 3 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = contentOrder.indexOf(active.id);
    const newIndex = contentOrder.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const newOrder = arrayMove(contentOrder, oldIndex, newIndex);

    // Only store if different from default
    const isDefault = newOrder.every((el, i) => el === DEFAULT_CONTENT_ORDER[i]);
    onChange(isDefault ? undefined : newOrder);
  };

  const handleDragCancel = () => {
    setActiveId(null);
  };

  // Check if current order differs from default
  const isCustomOrder = data.contentOrder && Array.isArray(data.contentOrder) && data.contentOrder.length > 0;

  return (
    <div className="field-container">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <div className="module-header">Content Order</div>
          <div className="module-desc">Drag to reorder elements on slide.</div>
        </div>
        {isCustomOrder && (
          <button
            type="button"
            title="Reset to default order"
            onClick={() => onChange(undefined)}
            className="p-1 rounded-md text-white/30 hover:text-white/70 hover:bg-white/10 transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 15 3 9m0 0 6-6M3 9h12a6 6 0 0 1 0 12h-3" />
            </svg>
          </button>
        )}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <SortableContext
          items={contentOrder}
          strategy={verticalListSortingStrategy}
        >
          <div className="content-order-list">
            {contentOrder.map((elementId) => (
              <SortableItem
                key={elementId}
                id={elementId}
                isActive={isElementActive(elementId, data)}
              />
            ))}
          </div>
        </SortableContext>

        <DragOverlay dropAnimation={null}>
          {activeId ? (
            <div className="content-order-slot content-order-slot-dragging">
              <ItemContent id={activeId} isActive={isElementActive(activeId, data)} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
